import React, { useRef, useState, useEffect } from "react";
import { ReactSketchCanvas } from "react-sketch-canvas";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import {
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  FormControl,
  Select,
  MenuItem,
  Snackbar
} from "@mui/material";
import SnackbarContent from "@mui/material/SnackbarContent";
import "./Popup.css";
import DrawIcon from "@mui/icons-material/Draw";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import eraserIcon from "../../images/eraser.png";
import Modal from "../Modal.jsx";
import { attachment, createIssue } from "../../network/apiService";

const PopUp = ({
  profileData,
  screenCapture,
  openCanvaModal,
  setCanvaOpenModal,
  videoRef,
  blobUrl,
  parentStyle,
  select,
  consumerId,
  isInsideChatbot,
  setIsBotHidden,
  isEmail,
  handleSendEmail
}) => {
  const canvasRef = useRef(null);
  const transformRef = useRef(null);
  const [tool, setTool] = useState("pencil");
  const [issueType, setIssueType] = useState("");
  const [impact, setImpact] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 500 });

  const resetForm = () => {
    setTool("pencil");
    setIssueType("");
    setImpact([]);
    setTitle("");
    setDescription("");
    setErrors({});
  };

  useEffect(() => {
    if (openCanvaModal) {
      resetForm();
    }
  }, [openCanvaModal]);

  useEffect(() => {
    const updateCanvasSize = () => {
      const width = window.innerWidth * 0.49;
      const height = window.innerHeight * 0.5;
      setCanvasSize({ width, height });
    };

    updateCanvasSize();
    window.addEventListener("resize", updateCanvasSize);
    return () => {
      window.removeEventListener("resize", updateCanvasSize);
    };
  }, []);

  const options = [
    { value: "P0", label: "Stops me from Doing my work (P0)" },
    { value: "P1", label: "Delays my work (P1)" },
    { value: "P2", label: "Minor inconvenience (P2)" }
  ];

  const handleImpactChange = (event) => {
    setImpact([event.target.value]);
  };

  const handleChange = (event, newAlignment) => {
    setIssueType(newAlignment);
  };

  const handleToolChange = (selectedTool) => {
    setTool(selectedTool);
    if (canvasRef.current) {
      if (selectedTool === "eraser") {
        canvasRef.current.eraseMode(true);
      } else {
        canvasRef.current.eraseMode(false);
      }
    }
  };

  const handleUndo = () => {
    if (canvasRef.current) {
      canvasRef.current.undo();
    }
  };

  const handleRedo = () => {
    if (canvasRef.current) {
      canvasRef.current.redo();
    }
  };

  const handleClear = () => {
    if (canvasRef.current) {
      canvasRef.current.clearCanvas();
    }
  };

  const handleZoomIn = () => {
    if (transformRef.current) {
      transformRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (transformRef.current) {
      transformRef.current.zoomOut();
    }
  };

  const drawOnCanvas = (tempCanvas, paths, img) => {
      const ctx = tempCanvas.getContext("2d");
      ctx.drawImage(img, 0, 0, tempCanvas.width, tempCanvas.height);

      const ratioX = tempCanvas.width / canvasSize.width;
      const ratioY = tempCanvas.height / canvasSize.height;

      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      paths.forEach((path) => {
        ctx.globalCompositeOperation = path.drawMode ? "source-over" : "destination-out";
        ctx.strokeStyle = path.strokeColor;
        ctx.lineWidth = path.strokeWidth * ratioX;

        ctx.beginPath();
        if (path.paths.length > 0) {
          ctx.moveTo(path.paths[0].x * ratioX, path.paths[0].y * ratioY);
          path.paths.slice(1).forEach((point) => {
            ctx.lineTo(point.x * ratioX, point.y * ratioY);
          });
          ctx.stroke();
        }
      });

    // Reset composite mode (to avoid unexpected eraser in future mapping)
    ctx.globalCompositeOperation = "source-over";
  }

  const handleSubmit = async () => {
    setCanvaOpenModal(false);
    const formData = new FormData();
    const newErrors = {};

    if (!issueType) newErrors.issueType = "Issue type is required";
    if (!impact.length) newErrors.impact = "Impact is required";
    if (!title) newErrors.title = "Title is required";
    if (!description) newErrors.description = "Description is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    if (select) {
      if (canvasRef.current) {
        // extracting vector paths user drew in SketchCanvas component to redraw(map) them on new image high resolution image we're creating below
        const paths = await canvasRef.current.exportPaths();
        const img = new Image();
        img.src = screenCapture;
        await new Promise((resolve) => (img.onload = resolve));

        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = img.naturalWidth;
        tempCanvas.height = img.naturalHeight;
        drawOnCanvas(tempCanvas, paths, img);

      const dataURL = tempCanvas.toDataURL("image/png");
      const base64Response = await fetch(dataURL);
      const blob = await base64Response.blob();
      formData.append("file", blob, "canvas_image.png");
      }
    } else {
      const response = await fetch(blobUrl);
      const blobContent = await response.blob();
      formData.append("file", blobContent, "video.mp4");
    }

    const descData = {
      profile: profileData,
      url: window.location.href,
      description
    };
    const stringifiedDescData = JSON.stringify(descData);

    const payload = {
      fields: {
        summary: title,
        issuetype: {
          name: issueType
        },
        description: stringifiedDescData,
        labels: impact,
        "customfield_10045": profileData?.user
      }
    };

    // Call API to create issue
    createIssue(consumerId, payload)
      .then((_response) => {
        // First API call completed successfully
        attachment(_response?.data.id, consumerId, formData)
          .then(() => {
            setOpenSnackbar(true);
          })
          .catch((error) => {
            console.error("Error in second API call:", error);
          });

        console.log("First API response:", _response.data);
      })
      .catch((error) => {
        // Handle errors from the first API call
        console.error("Error in first API call:", error);
      });

      if(isInsideChatbot) {
        setIsBotHidden(false);
      }
  };

  const handleClickSend = () => {
    setCanvaOpenModal(false); 
    handleSendEmail();
  }

  const isFormValid = issueType && impact.length && title && description;

  return (
    <>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={() => {
          return setOpenSnackbar(false);
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
      >
        <SnackbarContent
          message='Ticket Raised Successfully'
          sx={{ backgroundColor: "#2b826b" }}
        />
      </Snackbar>
      <Modal
        openModal={openCanvaModal}
        setOpenModal={setCanvaOpenModal}
        showCloseIcon
        customClass='CanvaModal'
        parentStyle={parentStyle}
      >
        <div className='CanvaMain'>
          {select ? (
            <div className='canvaArea' style={{ width: "70%" }}>
              <p
                className='heading'
                style={{
                  width: "300px",
                  height: "20px",
                  fontSize: "15px",
                  margin: "1% 47% 0 0",
                  fontWeight: "600",
                  fontStretch: "normal",
                  fontStyle: "normal",
                  lineHeight: "normal",
                  letterSpacing: "normal",
                  color: "#1c1c1c"
                }}
              >
                Screenshot Based Ticket
              </p>
              <hr className='partition' style={{ width: "100%" }} />
              <TransformWrapper
                ref={transformRef}
                panning={{
                  disabled: false,
                  lockAxisX: true,
                  lockAxisY: true
                }}
                pinch={{ disabled: false }}
                zoom={{
                  step: 0.1,
                  maxScale: 2,
                  minScale: 0.5
                }}
              >
                <TransformComponent>
                  <ReactSketchCanvas
                    ref={canvasRef}
                    height={`${canvasSize.height}px`}
                    width={`${canvasSize.width}px`}
                    backgroundImage={screenCapture}
                    cursor={tool}
                    preserveBackgroundImageAspectRatio="none"
                    style={{
                      boxShadow: "0 0 20px 4px rgba(0, 0, 0, 0.12)",
                      margin: "1px 2px 1px 2px",
                      padding: "1px 1px 1px 1px",
                      backgroundSize: "contain",
                      backgroundRepeat: "no-repeat"
                    }}
                    className='picture'
                  />
                </TransformComponent>
                <div
                  className='toolbox'
                  style={{ marginTop: "10px", height: "45px" }}
                >
                  <DrawIcon
                    className='tools'
                    onClick={() => {
                      return handleToolChange("pencil");
                    }}
                    disabled={tool === "pencil"}
                    style={{ backgroundColor: "#2d295b" }}
                  />
                  <img
                    className='tools'
                    onClick={() => {
                      return handleToolChange("eraser");
                    }}
                    src={eraserIcon}
                    style={{
                      width: "20px",
                      height: "20px",
                      marginTop: "2px",
                      marginRight: "0px",
                      backgroundColor: "#2d295b"
                    }}
                  />
                  <UndoIcon
                    className='tools'
                    onClick={handleUndo}
                    style={{ backgroundColor: "#2d295b" }}
                  />
                  <Button
                    className='clear'
                    style={{
                      border: "solid 1px #0568bf",
                      fontSize: "10px",
                      color: "#0568bf",
                      borderRadius: "7px"
                    }}
                    onClick={handleClear}
                  >
                    Clear
                  </Button>
                  <RedoIcon
                    className='tools'
                    onClick={handleRedo}
                    style={{ backgroundColor: "#2d295b" }}
                  />
                  <ZoomInIcon
                    className='tools'
                    onClick={handleZoomIn}
                    style={{ backgroundColor: "#2d295b" }}
                  />
                  <ZoomOutIcon
                    className='tools'
                    onClick={handleZoomOut}
                    style={{ backgroundColor: "#2d295b" }}
                  />
                </div>
              </TransformWrapper>
            </div>
          ) : (
            <div className='canvaArea' style={{ width: "75%" }}>
              <p
                className='heading'
                style={{
                  width: "300px",
                  height: "20px",
                  fontSize: "15px",
                  margin: "1% 47% 0 0",
                  fontWeight: "600",
                  fontStretch: "normal",
                  fontStyle: "normal",
                  lineHeight: "normal",
                  letterSpacing: "normal",
                  color: "#1c1c1c"
                }}
              >
                Recording Based Ticket
              </p>
              <hr className='partition' style={{ width: "100%" }} />
              <video
                ref={videoRef}
                src={blobUrl}
                height='95%'
                width='90%'
                style={{
                  boxShadow: "0 0 20px 4px rgba(0, 0, 0, 0.12)",
                  margin: "1px 3% 1% 5%",
                  margin: "1px 20px 1px 20px",
                  padding: "1px 1px 1px 1px"
                }}
                controls
                autoPlay
              />
            </div>
          )}
          {
            isEmail ? (
              <Button onClick={handleClickSend}>
              Send Media as Attachment
              </Button>
            ) : (
              <form
              className='formArea'
              style={{ marginTop: "-40px", backgroundColor: "var(--white)" }}
            >
              <div>
                <p className='issueType'>Issue Type</p>
                <ToggleButtonGroup
                  id='issueType'
                  color='primary'
                  value={issueType}
                  exclusive
                  onChange={handleChange}
                  aria-label='Platform'
                  style={{ gap: "16px" }}
                  className='IssueGroup'
                >
                  <ToggleButton
                    value='Bug'
                    style={{
                      borderRadius: "4px",
                      width: "100%",
                      height: "32px",
                      backgroundColor: issueType === "Bug" ? "#ffefe3" : "#eee",
                      color: issueType === "Bug" ? "#bf5505" : ""
                    }}
                  >
                    Bug
                  </ToggleButton>
                  <ToggleButton
                    className='issueBtntwo'
                    value='Task'
                    style={{
                      borderRadius: "4px",
                      width: "70%",
                      backgroundColor: issueType === "Task" ? "#ffefe3" : "#eee",
                      color: issueType === "Task" ? "#bf5505" : ""
                    }}
                  >
                    Enhancement
                  </ToggleButton>
                </ToggleButtonGroup>
                {errors.issueType && <p className='error'>{errors.issueType}</p>}
              </div>
              <div>
                <p className='impact' style={{ marginTop: "-5px" }}>
                  Impact
                </p>
                <FormControl fullWidth variant='outlined' size='small'>
                  <Select
                    labelId='impact-label'
                    id='impact'
                    value={impact}
                    onChange={handleImpactChange}
                    style={{
                      width: "100%",
                      height: "34px",
                      borderRadius: "4px",
                      backgroundColor: "var(--white)"
                    }}
                    MenuProps={{ style: { zIndex: 87654321345 } }}
                  >
                    {options.map((option) => {
                      return (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                {errors.impact && <p className='error'>{errors.impact}</p>}
              </div>
              <div>
                <p className='title' style={{ marginTop: "-5px" }}>
                  Title
                </p>
                <TextField
                  id='title'
                  variant='outlined'
                  size='small'
                  value={title}
                  onChange={(e) => {
                    return setTitle(e.target.value);
                  }}
                  style={{
                    width: "100%",
                    height: "34px",
                    borderRadius: "4px",
                    backgroundColor: "var(--white)"
                  }}
                />
                {errors.title && <p className='error'>{errors.title}</p>}
              </div>
              <div>
                <p className='description'>Description</p>
                <TextField
                  id='description'
                  variant='outlined'
                  size='small'
                  multiline
                  rows={4}
                  value={description}
                  onChange={(e) => {
                    return setDescription(e.target.value);
                  }}
                  style={{
                    width: "100%",
                    backgroundColor: "var(--white)"
                  }}
                />
                {errors.description && (
                  <p className='error'>{errors.description}</p>
                )}
              </div>
              <div style={{ display: "flex" }}>
                <Button
                  onClick={() => {
                    return setCanvaOpenModal(false);
                  }}
                  className='cancel'
                  style={{
                    width: "42px",
                    height: "18px",
                    fontSize: "12px",
                    fontWeight: "normal",
                    fontStretch: "normal",
                    fontStyle: "normal",
                    lineHeight: "normal",
                    letterSpacing: "normal",
                    textAlign: "center",
                    color: "#0568bf",
                    marginTop: "8px"
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  variant='contained'
                  className='ticket'
                  style={{
                    width: "140px",
                    height: "32px",
                    borderRadius: "4px",
                    fontWeight: "200",
                    fontSize: "12px",
                    backgroundColor: isFormValid ? "#0568bf" : "#cccccc",
                    color: isFormValid ? "#ffffff" : "#666666"
                  }}
                  disabled={!isFormValid}
                >
                  Raise Ticket
                </Button>
              </div>
            </form>
            )
          }
        </div>
      </Modal>
    </>
  );
};

export default PopUp;
